import React, { useMemo, useRef, useState } from 'react';

interface TooltipProps {
  children: React.ReactNode;
  content: React.ReactNode;
}

export function Tooltip({ children, content }: TooltipProps) {
  const anchorRef = useRef<HTMLDivElement>(null);
  const tooltipRef = useRef<HTMLDialogElement | null>(null);
  const [tooltipWidth, setTooltipWidth] = useState(0);
  const [tooltipHeight, setTooltipHeight] = useState(0);

  const position = useMemo(() => {
    const anchorRight = anchorRef?.current?.getBoundingClientRect().right || 0;
    const spaceRight = window.innerWidth - anchorRight;
    const horizontal =
      spaceRight <= tooltipWidth ? '-translate-x-full -left-3' : 'inset-x-auto translate-x-full -right-3';

    const anchorBottom = anchorRef?.current?.getBoundingClientRect().bottom || 0;
    const spaceBottom = window.innerHeight - anchorBottom;
    const vertical = spaceBottom <= tooltipHeight ? 'bottom-0' : 'top-0';

    return `${vertical} ${horizontal}`;
  }, [anchorRef.current, content]);

  const wrapWidth = useMemo(() => {
    const widthConfigs = {
      pixelBreakpoint: 384,
      wrapOverflowText: 'min-w-[384px] text-pretty',
      fitTooltipToText: 'max-w-[384px] text-nowrap',
    };
    const { pixelBreakpoint, wrapOverflowText, fitTooltipToText } = widthConfigs;

    return tooltipWidth < pixelBreakpoint ? fitTooltipToText : wrapOverflowText;
  }, [tooltipWidth]);

  const openTooltip = () => {
    if (tooltipRef.current) {
      tooltipRef.current.show();

      if (!tooltipWidth) setTooltipWidth(tooltipRef.current.getBoundingClientRect().width);
      if (!tooltipHeight) setTooltipHeight(tooltipRef.current.getBoundingClientRect().height);
    }
  };

  const hideTooltip = () => {
    if (tooltipRef.current) tooltipRef.current.close();
  };

  return (
    <div
      ref={anchorRef}
      tabIndex={0}
      className='relative h-fit w-fit'
      onMouseEnter={openTooltip}
      onMouseLeave={hideTooltip}
    >
      {children}
      <dialog
        ref={tooltipRef}
        role='tooltip'
        className={`${position} ${wrapWidth} z-[1] rounded-2 border-none bg-gray-700 px-3 py-2 text-sm text-white focus-visible:outline-none`}
      >
        {content}
      </dialog>
    </div>
  );
}
